import React, {
  useState, useCallback, useMemo, useEffect, useRef,
} from 'react';
import {
  Dialog,
  DialogContent,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useSwipeable } from 'react-swipeable';
import uuid from 'uuid';
import ScrollLock from 'react-scrolllock';
import { useLocation } from 'react-router';
import { isMobileOnly } from 'react-device-detect';
import clsx from 'clsx';
import { selectMediaViewer } from '../../../../../store/modules/articles/selectors';
import {
  setCurrentIndexMediaViewer,
  setOpenMediaViewer,
  setDataExportMedia,
  setOpenModalExportMedia,
  saveVideoThumbnailRequest,
  addDownloadMedia,
  addDocumentDownloadRequest,
  setNextArticleDocumentPage,
  setPreviousArticleDocumentPage,
  setCurrentDocumentPageIndexMediaViewer,
} from '../../../../../store/modules/articles/actions';
import { Article } from '../../../../../types/items';
import { useId } from '../../../../../hooks/index';
import MediaPreview from './MediaPreview';
import NextAction from './actions/nextAction';
import PreviousAction from './actions/previousAction';
import MediaPreviewStyles from './styles';
import keyCodeConverter from '../../../../../utility/keyCodeConverter';
import { getAllQueryData, updateQuery } from '../../../../../utility/helpers';
import MediaPreviewModalHeader from './MediaPreviewModalHeader/MediaPreviewModalHeader';

type Props = {
  mediaList: Article[];
};

const changePreviewMediaDelay = 500;

const MediaPreviewModal = ({ mediaList }: Props) => {
  const theme = useTheme();
  const classes = MediaPreviewStyles();
  const isMobile = isMobileOnly;
  const refPreviewContent = useRef<null | HTMLDivElement>(null);
  const id = useId();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { open, currentIndex } = useSelector(selectMediaViewer);
  const queryData = getAllQueryData();
  const media = mediaList[currentIndex];
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | 'up' | 'down'>('left');
  const [isChangePreviewMedia, setIsChangePreviewMedia] = useState<number | null>(null);
  const modalRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(true);
  const [fadeTimeoutId, setFadeTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [playerContainer, setPlayerContainer] = useState<{ height: string; width: string } | undefined>();

  useEffect(() => {
    if (open) {
      modalRef.current?.focus();
    }
  }, [open, modalRef]);

  const downloadHandler = useCallback(() => {
    const { document, video } = media.ref;
    if (document !== null) {
      dispatch(addDocumentDownloadRequest({
        documentId: document.id,
        mimeType: document.mime_type_id,
        title: document.ref.filename_base,
      }));
    } else if (video !== null && video.ref.statics) {
      const staticVideo = video.ref.statics[video.ref.statics.length - 1];
      if (staticVideo) {
        const ext = staticVideo.url.split('.').pop();
        const fileName = `${video.ref.filename_base}.${ext}`;
        dispatch(
          addDownloadMedia({
            url: staticVideo.url,
            name: fileName,
            downloadId: uuid(),
            urlRequested: false,
          }),
        );
      }
    }
  }, [dispatch, media]);

  const thumbnailHandler = useCallback(() => {
    if (media.permissions.can_edit) {
      const { video } = media.ref;
      if (video !== null) {
        const thumbnailTime = window.mediaPlayerRef?.currentTime;
        dispatch(
          saveVideoThumbnailRequest({
            id: video.id,
            thumbnail_timestamp: thumbnailTime === undefined ? 3 : thumbnailTime,
          } as any),
        );
      }
    }
  }, [dispatch, media]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const preview = urlParams.get('preview');
    const queryDocumentPageId = urlParams.get('documentPageId') || '';
    const documentPageId = parseInt(queryDocumentPageId, 10);

    if (preview) {
      const index = mediaList.findIndex((item) => item.id === Number(preview));
      if (documentPageId) {
        dispatch(setCurrentDocumentPageIndexMediaViewer(documentPageId));
      }
      dispatch(setCurrentIndexMediaViewer(index || 0));
      dispatch(setOpenMediaViewer(true));
    } else {
      dispatch(setOpenMediaViewer(false));
      dispatch(setCurrentIndexMediaViewer(0));
    }
  }, [location, dispatch, mediaList]);

  const totalMedia = useMemo(() => mediaList.length, [mediaList.length]);

  const onClose = useCallback(() => {
    if (isChangePreviewMedia !== null) {
      window.clearTimeout(isChangePreviewMedia);
      setIsChangePreviewMedia(null);
    }
    dispatch(setCurrentIndexMediaViewer(0));
    dispatch(setOpenMediaViewer(false));
    delete queryData.preview;
    delete queryData.documentPageId;
    updateQuery(queryData);
    setSlideDirection('left');
  }, [dispatch, queryData, setSlideDirection, isChangePreviewMedia, setIsChangePreviewMedia]);

  const nextMedia = useCallback(() => {
    let nextIndex = currentIndex + 1;
    if (nextIndex === totalMedia) {
      nextIndex = 0;
    }
    if (mediaList[nextIndex].id === undefined) {
      onClose();
      return;
    }
    queryData.preview = mediaList[nextIndex].id;
    if (isChangePreviewMedia !== null) {
      window.clearTimeout(isChangePreviewMedia);
    }
    updateQuery(queryData);
    setSlideDirection('right');
    const timeoutId = window.setTimeout(
      () => {
        dispatch(setCurrentDocumentPageIndexMediaViewer(1));
        dispatch(setCurrentIndexMediaViewer(nextIndex));
        setSlideDirection('left');
        setIsChangePreviewMedia(null);
      },
      changePreviewMediaDelay,
    );
    setIsChangePreviewMedia(timeoutId);
  }, [
    dispatch,
    currentIndex,
    totalMedia,
    mediaList,
    queryData,
    setSlideDirection,
    isChangePreviewMedia,
    setIsChangePreviewMedia,
    onClose,
  ]);

  const previousMedia = useCallback(() => {
    let previousIndex = currentIndex - 1;
    if (previousIndex < 0) {
      previousIndex = totalMedia - 1;
    }
    if (mediaList[previousIndex].id === undefined) {
      onClose();
      return;
    }
    queryData.preview = mediaList[previousIndex].id;
    updateQuery(queryData);
    setSlideDirection('left');
    if (isChangePreviewMedia !== null) {
      window.clearTimeout(isChangePreviewMedia);
    }
    const timeoutId = window.setTimeout(
      () => {
        dispatch(setCurrentIndexMediaViewer(previousIndex));
        dispatch(setCurrentDocumentPageIndexMediaViewer(1));
        setSlideDirection('right');
        setIsChangePreviewMedia(null);
      },
      changePreviewMediaDelay,
    );
    setIsChangePreviewMedia(timeoutId);
  }, [
    dispatch,
    currentIndex,
    totalMedia,
    mediaList,
    queryData,
    setSlideDirection,
    isChangePreviewMedia,
    setIsChangePreviewMedia,
    onClose,
  ]);

  const nextDocumentPage = useCallback(() => {
    if (media.ref.document?.media_type_id === 4) {
      if (isChangePreviewMedia !== null) {
        window.clearTimeout(isChangePreviewMedia);
      }
      setSlideDirection('down');
      const timeoutId = window.setTimeout(
        () => {
          setSlideDirection('up');
          setIsChangePreviewMedia(null);
          dispatch(setNextArticleDocumentPage());
        },
        changePreviewMediaDelay,
      );
      setIsChangePreviewMedia(timeoutId);
    }
  }, [dispatch, media, setSlideDirection, isChangePreviewMedia, setIsChangePreviewMedia]);

  const previousDocumentPage = useCallback(() => {
    if (media.ref.document?.media_type_id === 4) {
      if (isChangePreviewMedia !== null) {
        window.clearTimeout(isChangePreviewMedia);
      }
      setSlideDirection('up');
      const timeoutId = window.setTimeout(
        () => {
          setSlideDirection('down');
          setIsChangePreviewMedia(null);
          dispatch(setPreviousArticleDocumentPage());
        },
        changePreviewMediaDelay,
      );
      setIsChangePreviewMedia(timeoutId);
    }
  }, [dispatch, media, setSlideDirection, isChangePreviewMedia, setIsChangePreviewMedia]);

  const swipeable = useSwipeable({
    onSwipedLeft: nextMedia,
    onSwipedRight: previousMedia,
    onSwipedUp: nextDocumentPage,
    onSwipedDown: previousDocumentPage,
    delta: 100,
  });

  const openMediaExports = useCallback(() => {
    if (media) {
      dispatch(setDataExportMedia(media));
      dispatch(setOpenModalExportMedia(true));
    }
  }, [dispatch, media]);

  const onPlayerPauseHandler = useCallback(() => {
    if (fadeTimeoutId) {
      clearTimeout(fadeTimeoutId);
      setFadeTimeoutId(null);
    }

    setShowMenu(true);
  }, [setShowMenu, fadeTimeoutId, setFadeTimeoutId]);

  const onPlayerPlayHandler = useCallback(() => {
    const timeId = setTimeout(() => {
      setShowMenu(false);
    }, 2000);
    setFadeTimeoutId(timeId);
  }, [setShowMenu, setFadeTimeoutId]);

  const onResizePlayerHandler = useCallback((props) => {
    setPlayerContainer(props);
  }, [setPlayerContainer]);

  const keyHandler = useCallback((e: React.KeyboardEvent) => {
    if (e.ctrlKey) {
      switch (e.keyCode) {
        case keyCodeConverter('e'):
          if (media.ref.video) {
            e.preventDefault();
            openMediaExports();
          }
          break;

        case keyCodeConverter('d'):
          e.preventDefault();
          downloadHandler();
          break;

        case keyCodeConverter('m'):
          e.preventDefault();
          thumbnailHandler();
          break;

        default:
          break;
      }
    } else {
      switch (e.keyCode) {
        case keyCodeConverter('esc'):
          e.preventDefault();
          onClose();
          break;
        case keyCodeConverter('upArrow'):
          e.preventDefault();
          previousDocumentPage();
          break;
        case keyCodeConverter('downArrow'):
          e.preventDefault();
          nextDocumentPage();
          break;
        case keyCodeConverter('rightArrow'):
          e.preventDefault();
          nextMedia();
          break;
        case keyCodeConverter('leftArrow'):
          e.preventDefault();
          previousMedia();
          break;
        default:
          break;
      }
    }
  }, [
    onClose,
    previousMedia,
    nextMedia,
    thumbnailHandler,
    downloadHandler,
    openMediaExports,
    media,
    previousDocumentPage,
    nextDocumentPage,
  ]);

  const title = useMemo(() => {
    if (media && media.ref) {
      return media.ref.document?.title || media.ref.video?.title;
    }
    return null;
  }, [media]);

  if (mediaList.length === 0 || media === undefined) {
    if (open) {
      onClose();
    }
    return null;
  }

  return (
    <>
      <ScrollLock isActive={open} />
      <Dialog
        open={open}
        ref={modalRef}
        disableBackdropClick
        fullWidth
        aria-labelledby={id}
        maxWidth={compact ? 'xl' : 'lg'}
        disablePortal
        BackdropProps={{
          className: classes.backdrop,
        }}
        PaperProps={{
          className: classes.paperRoot,
        }}
        style={{ zIndex: 2147483100 }}
        onKeyDown={keyHandler}
        className={classes.noselect}
      >
        {!isMobile
        && (
          <MediaPreviewModalHeader
            thumbnailHandler={thumbnailHandler}
            downloadHandler={downloadHandler}
            media={media}
            title={title || ''}
            onClose={onClose}
          />
        )}
        <DialogContent className={clsx(classes.content, {
          [classes.contentMobile]: isMobile,
          [classes.contentVideoMobile]: (isMobile && media.video_id > 0),
        })}
        >
          {isMobile
            && (
              <MediaPreviewModalHeader
                thumbnailHandler={thumbnailHandler}
                downloadHandler={downloadHandler}
                media={media}
                title={title || ''}
                onClose={onClose}
                type="mobile"
                show={showMenu}
                playerContainer={playerContainer}
                swipeParameters={{
                  onSwipedLeft: nextMedia,
                  onSwipedRight: previousMedia,
                  onSwipedUp: nextDocumentPage,
                  onSwipedDown: previousDocumentPage,
                  delta: 100,
                }}
              />
            )}
          {!isMobile && <PreviousAction onClick={previousMedia} />}
          <div
            className={clsx(classes.mediaPreviewContainer, {
              [classes.mediaPreviewContainerMobile]: isMobile,
            })}
            ref={refPreviewContent}
          >
            <MediaPreview
              refPreviewContent={refPreviewContent}
              media={media}
              swipeable={swipeable}
              slideDirection={slideDirection}
              isChangePreviewMedia={isChangePreviewMedia}
              key={media.id}
              onPlayerPause={onPlayerPauseHandler}
              onPlayerPlay={onPlayerPlayHandler}
              onResizePlayer={onResizePlayerHandler}
            />
          </div>
          {!isMobile && <NextAction onClick={nextMedia} />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MediaPreviewModal;
